import axios from 'axios';

const SIX_DIGIT_PATH = '/sixDigitAuthorization';

/**
 * keyon answers 401 `mfa_required` when the session is valid but its MFA row
 * has lapsed (file.php, and cookie uploads under enforce). The page's own
 * "2FA verified" flag is then stale, so the user is sent back to enter a code.
 *
 * Only object bodies are read here. A file download asks for a Blob, reads its
 * own refusal and calls redirectToCodePage itself (api/keyonFiles.js). Images,
 * iframes and PDF.js fetches never pass through axios at all; those show
 * "Document unavailable" instead, and the next axios call redirects.
 */
export function isMfaRequired(error) {
  const res = error && error.response;
  if (!res || res.status !== 401) return false;
  const { data } = res;
  if (!data || typeof data !== 'object') return false;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return false;
  return data.code === 'mfa_required';
}

// jsdom's Blob (and older Safari's) has no text(); FileReader works everywhere.
function readBlobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * isMfaRequired for a request that asked for a Blob, whose refusal is a Blob
 * too and so invisible to the interceptor. pacmny-be's downloadCda reads its
 * own with this (api/cdaApi.js).
 * @returns {Promise<boolean>}
 */
export async function isMfaRequiredBlob(error) {
  const res = error && error.response;
  if (!res || res.status !== 401) return false;
  if (typeof Blob === 'undefined' || !(res.data instanceof Blob)) return false;
  try {
    const parsed = JSON.parse(await readBlobText(res.data));
    return Boolean(parsed) && parsed.code === 'mfa_required';
  } catch (_) {
    return false;
  }
}

function currentRoute(router) {
  const route = router && router.currentRoute;
  // vue-router 4 exposes a ref; tolerate a plain object too.
  return (route && 'value' in route ? route.value : route) || {};
}

// The store and router main.js installed, for callers that cannot inject them.
let installed = null;

/**
 * Back to the code page, remembering where the user was.
 * @returns {boolean} whether a redirect was started
 */
export function redirectToCodePage({ store, router } = installed || {}) {
  if (!store || !router) return false;
  const route = currentRoute(router);
  // Already on the code page: redirecting again would loop.
  if (route.path === SIX_DIGIT_PATH) return false;
  store.commit('login/SET_2FA_VERIFIED', false);
  store.commit('login/SET_INTENDED_ROUTE', route.fullPath || route.path || '/');
  const pushed = router.push(SIX_DIGIT_PATH);
  if (pushed && typeof pushed.catch === 'function') pushed.catch(() => {});
  return true;
}

/** Registers one response interceptor; every error is rethrown to its caller. */
export function installMfaRequiredHandler(client = axios, { store, router } = {}) {
  installed = { store, router };
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isMfaRequired(error)) redirectToCodePage({ store, router });
      return Promise.reject(error);
    },
  );
}
